import React, { useContext, useEffect } from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import LazyLoad from 'react-lazy-load';
import {
  splitSwimlanes,
  extractStrapiCarouselItems,
  injectTrailers,
} from '../utils/helpers';

import { AnalyticsContext } from '../analytics/AnalyticsContext';

// Components
import {
  BannerAdStatic,
  BannerAdDynamic,
  Carousel,
  Layout,
  SEO,
  Swimlane,
  ShopBannerCTA,
} from '../components';

// Styles
import './page.scss';

// TEALIUM
import initTealiumTracker from 'tealium-tracker';

let tealiumTrackPageLoad;
if (typeof window !== 'undefined') {
  const { trackPageLoad } = initTealiumTracker({ debugMode: true });
  tealiumTrackPageLoad = trackPageLoad;
}
const App = ({ pageContext }: any) => {
  const data = useStaticQuery(graphql`
    query HomePage {
      allStrapi {
        homePage {
          carousel {
            Slide {
              ...SlideFragment
            }
          }
          Advertisement {
            ...BannerAdFragment
          }
        }
      }

      allSOD {
        crescendoCarouselItems: curation(name: "OnDemandHomeCarousel") {
          items {
            assetType
            id
            altIds {
              tmsRootId
              tmsSeriesId
            }
            title
            imageUrl
            description
            shortDescription
            offer {
              providerName
              providerId
              networkImgUrl
              movieAssetId
              price {
                currency
                value
              }
            }
          }
        }

        carouselTrailers(curation: "OnDemandHomeCarousel", limit: 6) {
          id
          hls
          dash
          mp4
        }
      }
    }
  `);

  const { analytics } = useContext(AnalyticsContext);
  const { crescendoCarouselItems, carouselTrailers } = data.allSOD;
  const { carousel, Advertisement } = data.allStrapi.homePage;
  const { curations } = pageContext;

  // Adds trailer information to Carousel Items
  const slidesWithTrailers = injectTrailers(
    crescendoCarouselItems.items,
    carouselTrailers
  );

  // Carousel
  const strapiCarouselItems = extractStrapiCarouselItems(carousel);
  const slides = [...slidesWithTrailers, ...strapiCarouselItems];

  // Swimlanes
  const { firstSwimlaneHalf, secondSwimlaneHalf } = splitSwimlanes(curations);

  // Quantum Tracking for page view
  useEffect(() => {
    if (analytics) {
      analytics.track('SpectrumOnDemand_pageView_home', {
        currPageIsLazyLoad: true,
      });
    }
  }, [analytics]);

  // Tealium Page Tracking
  useEffect(() => {
    tealiumTrackPageLoad({
      pagename: 'Home Page',
      section: 'Home',
      pagetype: 'Home',
      sc_reporting_suite: 'charterspectrumondemand',
    });
  }, []);

  const analyticsConfig = {
    analytics,
    eventCase: 'SpectrumOnDemand_selectContent_curatedCatalog_home_selection',
  };

  return (
    <Layout
      header={<Carousel slides={slides} analytics={analytics} />}
      bottomBanner={
        <LazyLoad offsetVertical={0} throttle={250} debounce={false}>
          <BannerAdStatic version="spectrumGoes" />
        </LazyLoad>
      }
    >
      <SEO
        title="Find TV Shows, Movies, & Networks"
        description="Find all of your favorite TV shows, movies, networks, and pay per view events with Spectrum On Demand."
      />

      <ShopBannerCTA
        title="Interested in becoming a Spectrum TV Customer?"
        linkPath="https://www.spectrum.com/cable-tv/plans?cmp=sod_shop_home"
        tealiumConfig={{
          dataLinkType: 'shop-banner__primary-button',
          dataLinkName: `Shop Banner - Shop Spectrum's Plans`,
        }}
      />

      <h1 className="visually-hidden">Home</h1>

      {firstSwimlaneHalf.map(({ displayName, items }) => (
        <Swimlane
          key={displayName}
          title={displayName}
          items={items}
          analyticsConfig={analyticsConfig}
        />
      ))}

      <BannerAdDynamic
        ad={Advertisement.banner_ad}
        size={Advertisement.banner_ad.Size}
      />

      {secondSwimlaneHalf.map(({ displayName, items }) => (
        <Swimlane
          key={displayName}
          title={displayName}
          items={items}
          analyticsConfig={analyticsConfig}
        />
      ))}
    </Layout>
  );
};

export default App;
